/**
 * ChainVault WP - Referral Page
 * @package ChainVault
 */
import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';

export default function ReferralPage() {
    const { builtInWallet, address, balances } = useWallet();
    const b        = window.chainvaultSettings?.branding || {};
    const apiBase  = window.chainvaultSettings?.siteUrl  || '/wp-json/chainvault/v1';
    const appName  = b.app_name  || 'ChainVault';
    const refPct   = b.referral_pct || '20';
    const refMsg   = (b.referral_msg || 'Join me on {app_name} and earn crypto!').replace('{app_name}', appName);
    const enabled  = b.referral_enabled === '1';

    const [ code,     setCode     ] = useState(null);
    const [ stats,    setStats    ] = useState(null);
    const [ loading,  setLoading  ] = useState(true);
    const [ copied,   setCopied   ] = useState(false);

    const userAddress = builtInWallet?.solana?.address || builtInWallet?.ethereum?.address || address || '';
    const siteUrl     = window.location.origin + window.location.pathname;

    useEffect( () => {
        if ( !userAddress || !enabled ) { setLoading(false); return; }
        const init = async () => {
            try {
                /* Get or create referral code */
                const res  = await fetch( apiBase + '/referral/code', {
                    method: 'POST',
                    headers: { 'Content-Type':'application/json' },
                    body: JSON.stringify({ address: userAddress }),
                });
                const data = await res.json();
                setCode( data.code );

                /* Get stats */
                const sres  = await fetch( apiBase + '/referral/stats?address=' + userAddress );
                const sdata = await sres.json();
                setStats( sdata );
            } catch(e) { console.error(e); }
            setLoading(false);
        };
        init();
    }, [userAddress, enabled] );

    const referralLink = code ? `${siteUrl}?ref=${code}` : '';

    const copyLink = () => {
        navigator.clipboard.writeText(referralLink);
        setCopied(true);
        setTimeout(()=>setCopied(false), 2000);
    };

    const shareLink = (platform) => {
        const text = encodeURIComponent(refMsg + ' ' + referralLink);
        const urls = {
            twitter:  `https://twitter.com/intent/tweet?text=${text}`,
            telegram: `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(refMsg)}`,
            whatsapp: `https://wa.me/?text=${text}`,
        };
        if(urls[platform]) window.open(urls[platform], '_blank');
    };

    if (!enabled) return (
        <div style={{ maxWidth:'600px', margin:'60px auto', padding:'20px', textAlign:'center' }}>
            <div style={{ fontSize:'48px', marginBottom:'16px' }}>🔗</div>
            <h2 style={{ color:'#fff', marginBottom:'8px' }}>Referral Program</h2>
            <p style={{ color:'#555' }}>The referral program is not enabled yet. Check back soon!</p>
        </div>
    );

    if (!userAddress) return (
        <div style={{ maxWidth:'600px', margin:'60px auto', padding:'20px', textAlign:'center' }}>
            <div style={{ fontSize:'48px', marginBottom:'16px' }}>🔗</div>
            <h2 style={{ color:'#fff', marginBottom:'8px' }}>Referral Program</h2>
            <p style={{ color:'#555' }}>Connect your wallet to access your referral link.</p>
        </div>
    );

    return (
        <div style={{ maxWidth:'800px', margin:'0 auto', padding:'20px', fontFamily:'inherit' }}>
            <h1 style={{ color:'#fff', marginBottom:'4px', fontSize:'24px', fontWeight:'800' }}>🔗 Referral Program</h1>
            <p style={{ color:'#555', marginBottom:'24px', fontSize:'13px' }}>Earn {refPct}% of platform fees generated by people you refer</p>

            { /* How it works */ }
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'24px' }}>
                { [
                    { icon:'🔗', title:'Share Your Link', desc:'Share your unique referral link with friends' },
                    { icon:'👥', title:'They Join & Use', desc:'Your referrals bet, trade, create tokens and more' },
                    { icon:'💰', title:'You Earn', desc:`You get ${refPct}% of every fee they generate` },
                ].map((s,i)=>(
                    <div key={i} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'16px', textAlign:'center' }}>
                        <div style={{ fontSize:'28px', marginBottom:'8px' }}>{ s.icon }</div>
                        <div style={{ fontWeight:'700', color:'#fff', fontSize:'13px', marginBottom:'4px' }}>{ s.title }</div>
                        <div style={{ fontSize:'12px', color:'#555', lineHeight:'1.5' }}>{ s.desc }</div>
                    </div>
                ))}
            </div>

            { /* Stats */ }
            { stats && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'24px' }}>
                    { [
                        { label:'Total Referrals', value:stats.referred_count||0, color:'#6C63FF' },
                        { label:'Total Earned', value:(parseFloat(stats.total_earned||0).toFixed(6)), color:'#22c55e' },
                        { label:'Pending Payout', value:(parseFloat(stats.pending||0).toFixed(6)), color:'#f59e0b' },
                    ].map((s,i)=>(
                        <div key={i} style={{ background:'rgba(255,255,255,0.03)', border:`1px solid ${s.color}33`, borderRadius:'12px', padding:'16px', textAlign:'center' }}>
                            <div style={{ fontSize:'20px', fontWeight:'800', color:s.color }}>{ s.value }</div>
                            <div style={{ fontSize:'11px', color:'#555', marginTop:'4px', textTransform:'uppercase' }}>{ s.label }</div>
                        </div>
                    ))}
                </div>
            )}

            { /* Referral Link */ }
            { loading ? (
                <div style={{ textAlign:'center', padding:'40px', color:'#555' }}>Generating your referral link...</div>
            ) : code ? (
                <div style={{ background:'rgba(108,99,255,0.08)', border:'1px solid rgba(108,99,255,0.2)', borderRadius:'12px', padding:'20px', marginBottom:'24px' }}>
                    <div style={{ fontSize:'13px', color:'#888', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Your Referral Link</div>
                    <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                        <input readOnly value={referralLink}
                            style={{ flex:1, padding:'10px 14px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', color:'#fff', fontSize:'13px', fontFamily:'monospace', outline:'none' }} />
                        <button onClick={copyLink}
                            style={{ padding:'10px 16px', background: copied?'rgba(34,197,94,0.2)':'rgba(108,99,255,0.2)', border:`1px solid ${copied?'rgba(34,197,94,0.4)':'rgba(108,99,255,0.4)'}`, borderRadius:'8px', color: copied?'#22c55e':'#a78bfa', cursor:'pointer', fontFamily:'inherit', fontWeight:'700', fontSize:'13px', whiteSpace:'nowrap' }}>
                            { copied ? '✅ Copied!' : '📋 Copy' }
                        </button>
                    </div>
                    <div style={{ fontSize:'12px', color:'#555', marginBottom:'12px' }}>Share on:</div>
                    <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                        { [
                            { id:'twitter',  icon:'🐦', label:'Twitter/X' },
                            { id:'telegram', icon:'✈️', label:'Telegram' },
                            { id:'whatsapp', icon:'💬', label:'WhatsApp' },
                        ].map(p=>(
                            <button key={p.id} onClick={()=>shareLink(p.id)}
                                style={{ padding:'8px 16px', background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', color:'#ddd', cursor:'pointer', fontFamily:'inherit', fontSize:'12px', display:'flex', alignItems:'center', gap:'6px' }}>
                                { p.icon } { p.label }
                            </button>
                        ))}
                    </div>
                </div>
            ) : null}

            { /* Earnings History */ }
            { stats?.earnings?.length > 0 && (
                <div>
                    <h3 style={{ color:'#fff', fontSize:'15px', fontWeight:'700', marginBottom:'12px' }}>📊 Earnings History</h3>
                    { stats.earnings.map((e,i)=>(
                        <div key={i} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'12px 16px', marginBottom:'8px', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'8px' }}>
                            <div>
                                <span style={{ padding:'2px 8px', borderRadius:'4px', background:'rgba(108,99,255,0.15)', color:'#a78bfa', fontSize:'11px', fontWeight:'700', marginRight:'8px' }}>{ e.action_type }</span>
                                <span style={{ fontSize:'12px', color:'#555' }}>from { e.referred_address.slice(0,12) }...</span>
                            </div>
                            <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                                <span style={{ fontSize:'13px', fontWeight:'700', color:'#22c55e' }}>+{ parseFloat(e.bonus_amount).toFixed(6) } { e.network }</span>
                                <span style={{ padding:'2px 8px', borderRadius:'4px', fontSize:'11px', fontWeight:'700', background:e.status==='paid'?'rgba(34,197,94,0.15)':'rgba(245,158,11,0.15)', color:e.status==='paid'?'#22c55e':'#f59e0b' }}>
                                    { e.status.toUpperCase() }
                                </span>
                            </div>
                        </div>
                    ))}
                    <div style={{ fontSize:'12px', color:'#444', marginTop:'8px', textAlign:'center' }}>
                        Pending bonuses are paid manually by admin to your wallet address
                    </div>
                </div>
            )}
        </div>
    );
}
